import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { GlobalContext } from "../contexts/GlobalContext";
import {
  fetchLatestUserProgress,
  fetchUserName,
  getGroupDetailsFromFirestore,
} from "../services/firebase";
import { GroupMember } from "../classes/GroupMemberProgress";
import Add01Icon from "../assets/icons/add-01-solid-standard";
import InviteNewMemberToGroup from "./ui/InviteNewMemberToGroup";
import { snackBarConfigType } from "../classes/SnackBarConfig";
import GroupProgressCard from "./ui/GroupProgressCard";
import { MemberProgress } from "./ui/GroupProgressCard";
import { VersesInfo, QURAN_SURAH } from "../classes/UserProgress";

const GroupDetails = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const [group, setGroup] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [inviteWindowVisible, setInviteWindowVisible] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { currentUser } = useContext(GlobalContext);
  const [membersProgress, setMembersProgress] = useState<MemberProgress[]>([]);

  useEffect(() => {
    const fetchGroup = async () => {
      if (!groupId) return;
      setLoading(true);
      try {
        const groupData = await getGroupDetailsFromFirestore(groupId);
        setGroup(groupData);
        console.log("groupData: ", groupData);
      } catch (err) {
        setError("Failed to load group details.");
      }
      setLoading(false);
    };
    fetchGroup();
  }, [groupId]);

  useEffect(() => {
    const fetchMembersLatestProgress = async () => {
      const memberIds = group?.members || [];
      if (memberIds.length === 0) return;

      try {
        const results = await Promise.all(
          memberIds.map(async (memberId: string) => {
            const latestProgress = await fetchLatestUserProgress(memberId);
            if (!latestProgress) return null;
            const memberInfo = await fetchUserName(memberId);
            return {
              id: latestProgress.id,
              memberInfo,
              progressFrom: latestProgress.progressFrom,
              progressTo: latestProgress.progressTo,
            };
          })
        );

        const filteredResults = results.filter(
          (progress): progress is MemberProgress => progress !== null
        );

        setMembersProgress(filteredResults);
      } catch (error) {
        console.error("Error fetching member progress:", error);
      }
    };

    if (group) fetchMembersLatestProgress();
  }, [group]);

  const getVerseRank = (progress: VersesInfo): number => {
    return progress.chapter * 1000 + progress.verse;
  };

  const sortedLeaderboard = [...membersProgress].sort(
    (a, b) => getVerseRank(b.progressTo) - getVerseRank(a.progressTo)
  );

  const getSurahName = (chapter: number) => {
    const surah = QURAN_SURAH.find((s) => s.number === chapter);
    return surah ? surah.name : `Surah ${chapter}`;
  };

  // ----------------------------------------
  if (loading)
    return (
      <div className="mx-4 my-10">
        <h1 className="font-grgrifter text-[2.2rem] tracking-wider text-[#AFAEB3] mb-10 w-full text-center">
          Loading group details...
        </h1>
        <button
          className="flex items-left justify-left px-3 py-2 rounded-[2em] cursor-pointer ml-2"
          type="submit"
          onClick={() => navigate("/group")}
        >
          <p className="font-grgrifter text-sm tracking-widest text-left text-gray-500">
            {"< Back"}
          </p>
        </button>
      </div>
    );

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="px-4 py-8 bg-[#F1EFF6] min-h-screen">
      <h1 className="font-grgrifter text-[3rem] text-[#AFAEB3] text-center mb-8">
        {group?.name}
      </h1>

      <button
        className=" flex items-left justify-left px-3 py-2 rounded-[2em] cursor-pointer ml-2"
        type="submit"
        onClick={() => navigate("/group")}
      >
        {/* <ShowIcon size={18} className="mr-2" color="#3A9437" /> */}
        <p className="font-grgrifter text-sm tracking-widest text-left text-gray-500">
          {"< Back"}
        </p>
      </button>

      {/* Leaderboard Section */}
      <div className="bg-white rounded-[2rem] p-6 mx-auto max-w-md mb-10">
        <h2 className="font-grgrifter text-2xl text-[#AFAEB3] mb-6">
          Leader Board
        </h2>

        <div className="flex justify-start pl-4 gap-x-6 relative items-end">
          {sortedLeaderboard.slice(0, 3).map((member, index) => {
            const size = ["w-20 h-20", "w-16 h-16", "w-14 h-14"];
            const marginTop = ["mt-0", "mt-6", "mt-12"];
            const lineHeight = ["h-24", "h-20", "h-10"];
            const bgColor = [
              "bg-[#1B8017]",
              "bg-[#1B8017]/80",
              "bg-[#1B8017]/60",
            ];
            const positionText = ["1st", "2nd", "3rd"];
            return (
              <div key={member.id} className="flex flex-col items-start">
                <div className="flex flex-col justify-center items-center">
                  <div
                    className={`${size[index]} ${bgColor[index]} ${marginTop[index]} rounded-full flex items-center justify-center text-white text-lg font-semibold`}
                  >
                    {positionText[index]}
                  </div>
                  <p className={`mt-2 mb-4 text-sm`}>
                    {member.memberInfo?.userName.split(" ")[0]}
                  </p>

                  <div
                    className={`!w-0.5  bg-[#B1B0B4]`}
                    style={{
                      width: "2px",
                      height:
                        index === 0 ? "96px" : index === 1 ? "80px" : "40px",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* <div className="flex items-end justify-around relative h-40">
          {sortedLeaderboard.slice(0, 3).map((member, index) => {
            const sizeClass = [
              "mb-10 w-20 h-20",
              "w-16 h-16",
              "mb-4 w-14 h-14",
            ];
            const bgColor = ["bg-green-700", "bg-green-500", "bg-green-400"];
            const positionText = ["1st", "2nd", "3rd"];
            return (
              <div key={member.id} className="flex flex-col items-center">
                <div
                  className={`${sizeClass[index]} ${bgColor[index]} rounded-full flex items-center justify-center text-white text-lg font-semibold`}
                >
                  {positionText[index]}
                </div>
                <p className="mt-2 text-sm">{member.memberInfo?.userName}</p>
              </div>
            );
          })}
        </div> */}

        {/* <div className="flex items-end justify-around relative h-40">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white text-lg font-semibold">
              2nd
            </div>
            <p className="mt-2 text-sm">Mustaq</p>
          </div>
          <div className="flex flex-col items-center mb-10">
            <div className="w-20 h-20 bg-green-700 rounded-full flex items-center justify-center text-white text-lg font-semibold">
              1st
            </div>
            <p className="mt-2 text-sm">RiyazAhamed</p>
          </div>
          <div className="flex flex-col items-center mb-4">
            <div className="w-14 h-14 bg-green-400 rounded-full flex items-center justify-center text-white text-lg font-semibold">
              3rd
            </div>
            <p className="mt-2 text-sm">Ahmed</p>
          </div>
        </div> */}
      </div>

      {/* Members Section */}
      <div className="mb-6">
        <h2 className="text-xl text-[#1B1B1B] mb-4">Members:</h2>
        <div className="flex items-center gap-4">
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => setInviteWindowVisible(true)}
          >
            <div className="w-12 h-12 rounded-full bg-white border flex items-center justify-center text-2xl text-green-500">
              +
            </div>
            <p className="text-xs mt-[0.5em]">Add</p>
          </div>
          {group?.memberDetails?.map((member: GroupMember, i: number) => (
            <div key={i} className="flex flex-col items-center">
              {member.profilePicURL && (
                <img
                  src={member.profilePicURL}
                  alt={member.userName}
                  className="w-12 h-12 rounded-full"
                />
              )}
              {/* <div className="w-12 h-12 rounded-full bg-gray-300"></div> */}
              <p className="text-xs mt-[0.5em]">
                {member.userName.split(" ")[0]}
              </p>
            </div>
          ))}
        </div>
        {groupId && inviteWindowVisible && (
          <InviteNewMemberToGroup
            groupId={groupId}
            userId={currentUser.id}
            createdGroupName={group.name}
            currentUser={currentUser}
            setShowInvitation={setInviteWindowVisible}
            setSnackBarConfig={function (value: snackBarConfigType): void {
              throw new Error("Function not implemented.");
            }}
          />
        )}
      </div>

      <GroupProgressCard
        key={group.id}
        cardTitle={""}
        memberIds={group.members}
      />
    </div>
  );
};

export default GroupDetails;

// const GroupDetails = () => {
//   const { groupId } = useParams<{ groupId: string }>();
//   const [group, setGroup] = useState<any>(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");

//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchGroup = async () => {
//       if (!groupId) return;
//       setLoading(true);
//       try {
//         const groupData = await getGroupDetailsFromFirestore(groupId);
//         setGroup(groupData);
//         console.log("groupData: ", groupData);
//       } catch (err) {
//         setError("Failed to load group details.");
//       }
//       setLoading(false);
//     };

//     fetchGroup();
//   }, [groupId]);

//   if (loading)
//     return (
//       <>
//         <div className="mx-4 my-10">
//           <h1 className="font-grgrifter text-[2.2rem] tracking-wider text-[#AFAEB3] mb-10 w-full text-center">
//             Loading group details...
//           </h1>
//           <button
//             className=" flex items-left justify-left px-3 py-2 rounded-[2em] cursor-pointer ml-2"
//             type="submit"
//             onClick={() => navigate("/group")}
//           >
//             {/* <ShowIcon size={18} className="mr-2" color="#3A9437" /> */}
//             <p className="font-grgrifter text-sm tracking-widest text-left text-gray-500">
//               {"< Back"}
//             </p>
//           </button>
//         </div>
//       </>
//     );
//   if (error) return <p className="text-red-500">{error}</p>;

//   return (
//     <>
//       <div className="mx-4 my-10">
//         <h1 className="font-grgrifter text-[2.2rem] tracking-wider text-[#AFAEB3] mb-10 w-full text-center">
//           {group?.name}
//         </h1>
//         <button
//           className=" flex items-left justify-left px-3 py-2 rounded-[2em] cursor-pointer ml-2"
//           type="submit"
//           onClick={() => navigate("/group")}
//         >
//           {/* <ShowIcon size={18} className="mr-2" color="#3A9437" /> */}
//           <p className="font-grgrifter text-sm tracking-widest text-left text-gray-500">
//             {"< Back"}
//           </p>
//         </button>
//         {group?.createdAt && (
//           <p className="text-gray-600">
//             Created on: {new Date(group.createdAt).toLocaleString()}
//           </p>
//         )}

//         <h2 className="mx-4  text-2xl text-[#1B1B1B] mb-4">Members:</h2>

//         <div className="flex items-center">
//           {/* Add New Member Button */}
//           {/* <div className="flex flex-col justify-center items-center gap-2 mx-2">
//             <button
//               className="flex flex-col justify-center items-center bg-[#3A9437] w-12 h-12 rounded-full hover:bg-[#2e7d32] transition-all"
//               onClick={() => console.log("Open invite member modal")}
//             >
//               <Add01Icon size={18} color="#FFF" />
//             </button>
//             <span className="text-[0.6em]">Add</span>
//           </div> */}

//           {/* Members List */}
//           {/* <div className="flex items-center "> */}
//           {group?.memberDetails?.map((member: GroupMember, index: number) => (
//             <div
//               key={index}
//               className="flex flex-col justify-center items-center gap-2 mx-2"
//             >
//               {member.profilePicURL && (
//                 <img
//                   src={member.profilePicURL}
//                   alt={member.userName}
//                   className="w-12 h-12 rounded-full"
//                 />
//               )}
//               <span className="text-[0.6em]">
//                 {member.userName.split(" ")[0]}
//               </span>
//             </div>
//           ))}
//         </div>

//         {/* <InviteNewMemberToGroup
//           groupId={groupId}
//           userId={}
//           createdGroupName={""}
//           currentUser={undefined}
//           setSnackBarConfig={function (value: snackBarConfigType): void {
//             throw new Error("Function not implemented.");
//           }}
//         /> */}
//         {/* </div> */}
//       </div>
//     </>
//   );
// };
